
import { TimerState, TimerMode } from '../types';

// Declare chrome API
declare const chrome: any;

const STORAGE_KEY = 'eisenhower_timer_state';
const ALARM_NAME = 'eisenhower_focus_timer';

const isExtension = typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local;

// Default initial state
const initialState: TimerState = {
  status: 'idle',
  mode: 'work',
  endTime: null,
  remainingSeconds: 25 * 60,
  totalDuration: 25 * 60,
  taskId: null,
  accumulatedTime: 0,
  lastUpdated: Date.now()
};

// --- CORE FUNCTIONS ---

export const getTimerState = async (): Promise<TimerState> => {
  if (isExtension) {
    return new Promise((resolve) => {
      chrome.storage.local.get([STORAGE_KEY], (result: any) => {
        if (result[STORAGE_KEY]) {
          resolve(result[STORAGE_KEY] as TimerState);
        } else {
          resolve(initialState);
        }
      });
    });
  } else {
    // Web Fallback
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : initialState;
  }
};

const saveTimerState = async (state: TimerState) => {
  if (isExtension) {
    await chrome.storage.local.set({ [STORAGE_KEY]: state });
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }
};

// Start or Resume the Timer
export const startTimer = async (
  currentDurationSeconds: number, 
  mode: TimerMode, 
  taskId: string
) => {
  const now = Date.now();
  const currentState = await getTimerState();
  
  // Calculate end time
  // If resuming (paused), use stored remainingSeconds. 
  // If new start (idle), use passed duration.
  const secondsToRun = currentState.status === 'paused' 
    ? currentState.remainingSeconds 
    : currentDurationSeconds;

  const endTime = now + (secondsToRun * 1000);

  const newState: TimerState = {
    ...currentState,
    status: 'running',
    mode,
    taskId,
    endTime,
    totalDuration: currentState.status === 'paused' ? currentState.totalDuration : currentDurationSeconds,
    lastUpdated: now
  };

  await saveTimerState(newState);

  // Set Alarm in Chrome (This wakes up background.js when time is up)
  if (isExtension && chrome.alarms) {
    chrome.alarms.create(ALARM_NAME, { when: endTime });
  }

  return newState;
};

// Pause the Timer
export const pauseTimer = async () => {
  const currentState = await getTimerState();
  
  if (currentState.status !== 'running' || !currentState.endTime) return currentState;

  const now = Date.now();
  const remainingMs = currentState.endTime - now;
  const remainingSeconds = Math.max(0, Math.ceil(remainingMs / 1000));

  // Calculate accumulated work time so far
  // accumulated = previous_accumulated + (now - last_update)
  // note: simplified logic, usually we track start time of segment
  // For better precision, we'd assume continuous run since lastUpdated
  const segmentDuration = Math.max(0, (now - currentState.lastUpdated) / 1000);

  const newState: TimerState = {
    ...currentState,
    status: 'paused',
    endTime: null,
    remainingSeconds,
    accumulatedTime: currentState.accumulatedTime + segmentDuration,
    lastUpdated: now
  };

  await saveTimerState(newState);

  if (isExtension && chrome.alarms) {
    chrome.alarms.clear(ALARM_NAME);
  }

  return newState;
};

// Reset/Stop the Timer
export const resetTimerService = async (defaultDuration: number, mode: TimerMode) => {
  // If we were running, calculate final accumulated time before resetting
  const currentState = await getTimerState();
  let finalAccumulated = currentState.accumulatedTime;
  
  if (currentState.status === 'running' && currentState.endTime) {
     const now = Date.now();
     // Determine if we finished or stopped early
     const actualEnd = Math.min(now, currentState.endTime);
     const segment = Math.max(0, (actualEnd - currentState.lastUpdated) / 1000);
     finalAccumulated += segment;
  }

  const newState: TimerState = {
    ...initialState,
    status: 'idle',
    mode,
    remainingSeconds: defaultDuration,
    totalDuration: defaultDuration,
    accumulatedTime: 0, // Reset for next session, but return previous value for logging
    lastUpdated: Date.now()
  };

  await saveTimerState(newState);

  if (isExtension && chrome.alarms) {
    chrome.alarms.clear(ALARM_NAME);
  }

  return { newState, capturedTime: finalAccumulated };
};

// Called periodically by UI to ensure state is consistent (e.g. if alarm fired in background)
export const syncTimerState = async (): Promise<TimerState> => {
  const state = await getTimerState();
  
  // Logic to auto-transition from running to finished if time passed
  if (state.status === 'running' && state.endTime && Date.now() >= state.endTime) {
     const newState: TimerState = {
       ...state,
       status: 'finished',
       remainingSeconds: 0,
       accumulatedTime: state.accumulatedTime + (state.totalDuration - state.accumulatedTime), // Cap at total
       endTime: null
     };
     await saveTimerState(newState);
     return newState;
  }
  
  return state;
};
